import { React, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createHttpClient, handleRemoteError } from '../utilities/httpclient';
import { AbdsButton, AbdsCheckbox } from '@abds/react-bindings';
import createUserSessionActivity from '../utilities/usersessiontracking';
import { createDisplayAreaMessengerContext, DisplayAreaMessenger } from './displayareamessenger/DisplayAreaMessenger';
import { APPLICATION_API_ENDPOINT } from '../utilities/constants';
import '../styles/absgtable.css';

export function ExpirationEventPartners(props) {
    const navigate = useNavigate();
    const params = useParams();

    const [showLoading, setShowLoading] = useState(true);
    const [expirationEventGUID, setExpirationEventGUID] = useState(params.expirationEventGUID);
    const [expirationEventPartnerList, setExpirationEventPartnerList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showActiveOnly, setShowActiveOnly] = useState(true); // Filter checked by default
    let [messageContext, setMessageContext] = useState(createDisplayAreaMessengerContext());

    // Apply filter before pagination
    const filteredList = showActiveOnly
        ? expirationEventPartnerList.filter(item => item.active === true)
        : expirationEventPartnerList;

    // Calculate pagination values based on filtered data
    const totalItems = filteredList.length;
    const totalPages = Math.ceil(totalItems / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = filteredList.slice(startIndex, endIndex);

    const getExpirationEventPartnersByExpirationEventGUID = async eeGUID => {
        setMessageContext({});
        const httpclient = createHttpClient();
        const criteria = {
            'ExpirationEventGUID': eeGUID,
            'ExpirationEvent_PartnersGUID': null,
            'Active': null
        };
        await httpclient.post(`${APPLICATION_API_ENDPOINT}/certificate/expirationeventpartnerstatuses`, criteria).then(response => {
            setShowLoading(false);
            setExpirationEventGUID(eeGUID);
            const data = response?.data || [];
            setExpirationEventPartnerList(data);
            setCurrentPage(1); // Reset to first page when data loads

            if (data.length === 0) {
                messageContext.information.message = "There are no partners associated with this expiration event";
                setMessageContext(messageContext);
            }
        })
            .catch(e => (handleRemoteError(navigate, e) || showErrorMessage(e.response?.data || e.message)));
    };

    const onClickGoToCertificateManagementDetails = e => {
        e.preventDefault();

        if (!expirationEventGUID)
            return;

        navigate(`/certificatedetails/${expirationEventGUID}`);
        return;
    };

    const showErrorMessage = msg => {
        setMessageContext({});
        messageContext = createDisplayAreaMessengerContext();
        messageContext.error.message = msg;
        setMessageContext(messageContext);
    };

    const onClickSelectPartner = async e => {
        e.preventDefault();
        setMessageContext({});
        messageContext = createDisplayAreaMessengerContext();
        const label = e.target.getAttribute('data-label');
        const index = +e.target.getAttribute('data-index');
        const newvalue = !!e.target.checked ? 'true' : 'false';
        const criteria = {
            'ExpirationEventGUID': null,
            'ExpirationEventPartnerGUID': e.target.getAttribute('data-key'),
            'Active': !!e.target.checked
        };
        createUserSessionActivity(window.location.pathname, 'onClickSelectPartner', criteria);
        const httpclient = createHttpClient();
        await httpclient.post(`${APPLICATION_API_ENDPOINT}/certificate/saveexpirationeventpartnerstatus`, criteria).then(response => {
            expirationEventPartnerList[index].active = criteria.Active;
            setExpirationEventPartnerList([...expirationEventPartnerList]); // Create new array to trigger re-render
            messageContext.success.message = `Status for ${label} successfully changed to ${newvalue}!`;
            setMessageContext(messageContext);
        })
            .catch(e => (handleRemoteError(navigate, e) || showErrorMessage(e.response?.data || e.message)));
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1); // Reset to first page when page size changes
    };

    const handleFilterChange = (e) => {
        setShowActiveOnly(e.target.checked);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    useEffect(() => {
        createUserSessionActivity(window.location.pathname);
        getExpirationEventPartnersByExpirationEventGUID(expirationEventGUID);
    }, []);

    useEffect(() => {
        const items = document.getElementsByTagName('abds-checkbox');
        Array.from(items || []).forEach(item => {
            item.removeEventListener('abdsChange', onClickSelectPartner); //JUST IN CASE something is attached
            item.addEventListener('abdsChange', onClickSelectPartner);
        });
        return () => {
            const items = document.getElementsByTagName('abds-checkbox');
            Array.from(items || []).forEach(item => item.removeEventListener('abdsChange', onClickSelectPartner));
        };
    }, [currentPageData]);

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div>
            <h3>Expiration Event Partners Management</h3>

            <hr className="abcblue" />

            <DisplayAreaMessenger context={messageContext} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                    <AbdsButton onClick={onClickGoToCertificateManagementDetails}>
                        Return to Certificate Management Details
                    </AbdsButton>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <input
                        type="checkbox"
                        id="showActiveOnly"
                        checked={showActiveOnly}
                        onChange={handleFilterChange}
                        style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor="showActiveOnly" style={{ cursor: 'pointer', marginBottom: 0 }}>
                        Show Active only
                    </label>
                </div>
            </div>

            <hr className="abcblue" />

            <p>Choose from the list below the partners who should receive email notifications associated with this certificate.</p>
            {
                !!showLoading &&
                <p className="centered">Loading partner list, please wait...</p>
            }
            {
                !showLoading && expirationEventPartnerList.length === 0 &&
                <></>
            }
            {
                !showLoading && filteredList.length === 0 && expirationEventPartnerList.length > 0 &&
                <p className="centered">No active partners found. Uncheck the filter to see all partners.</p>
            }
            {
                !showLoading && filteredList.length > 0 &&
                <>
                    <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} partners
                            {showActiveOnly && <span style={{ fontStyle: 'italic', marginLeft: '5px' }}>(Active only)</span>}
                        </div>
                        <div>
                            <label htmlFor="pageSize">Items per page: </label>
                            <select id="pageSize" value={pageSize} onChange={handlePageSizeChange}>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>
                    <table className="absgtable auxiliary">
                        <thead>
                            <tr>
                                <th>Partner Name</th>
                                <th>Application Type</th>
                                <th>Application Type Reference</th>
                                <th className="centered">Active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                currentPageData.map((item, i) => {
                                    const actualIndex = expirationEventPartnerList.indexOf(item); // Find actual index in full list
                                    return (
                                        <tr key={item.expirationEvent_PartnersGUID || actualIndex}>
                                            <td>{item.partnerName}</td>
                                            <td>{item.applicationTypeLabel}</td>
                                            <td>{item.applicationTypeReference}</td>
                                            <td className="centered">
                                                <AbdsCheckbox
                                                    data-index={actualIndex}
                                                    data-label={item.partnerName}
                                                    data-key={item.expirationEvent_PartnersGUID}
                                                    name={`selectpartner${actualIndex}`}
                                                    checked={item.active}>
                                                </AbdsCheckbox>
                                            </td>
                                        </tr>)
                                })
                            }
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                style={{ padding: '5px 10px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                                First
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{ padding: '5px 10px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                                Previous
                            </button>
                            {getPageNumbers().map(pageNum => (
                                <button
                                    key={pageNum}
                                    onClick={() => handlePageChange(pageNum)}
                                    style={{
                                        padding: '5px 10px',
                                        fontWeight: pageNum === currentPage ? 'bold' : 'normal',
                                        backgroundColor: pageNum === currentPage ? '#007bff' : 'white',
                                        color: pageNum === currentPage ? 'white' : 'black',
                                        cursor: 'pointer'
                                    }}>
                                    {pageNum}
                                </button>
                            ))}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage >= totalPages}
                                style={{ padding: '5px 10px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}>
                                Next
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage >= totalPages}
                                style={{ padding: '5px 10px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}>
                                Last
                            </button>
                        </div>
                    )}
                </>
            }
            {
                !showLoading && expirationEventPartnerList.length > 0 &&
                <>
                    <hr className="abcblue" />
                    <p>
                        <AbdsButton onClick={onClickGoToCertificateManagementDetails}>
                            Return to Certificate Management Details
                        </AbdsButton>
                    </p>
                </>
            }
        </div>
    );
}